import { html, nothing } from "lit";
import {
  buildActivityStream,
  collectActivityState,
  filterActivityStream,
  type ActivityStreamEntry,
  type ActivityStreamKind,
} from "../chat/chat-activity-collect.ts";
import type { FleetTodoItem } from "../controllers/fleet-todos.ts";
import type { RunTimelineEntry } from "../operator-trust/state.ts";
import type { ChatSubagentCard } from "../ui-types.ts";

/** Filter chip ids for the unified Activity stream (`explore` kept as alias → `exec`). */
export type ChatActivityTab = "all" | "thinking" | "exec" | "explore" | "todo" | "health";

export type ChatActivityPanelProps = {
  tab: ChatActivityTab;
  onTabChange: (tab: ChatActivityTab) => void;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  messages: unknown[];
  chatToolMessages: unknown[];
  stream: string | null;
  reasoningStream?: string | null;
  subagents: ChatSubagentCard[];
  showThinking: boolean;
  runActive?: boolean;
  liveRunHint?: string | null;
  activeFileToolPath?: string | null;
  fleetTodos?: FleetTodoItem[];
  connected?: boolean;
  runTimeline?: RunTimelineEntry[];
  sessionHealth?: "healthy" | "suspect" | "wedged" | null;
  sessionHealthReason?: string | null;
  turnPhase?: string | null;
  onOpenSubagentSession?: (childSessionKey: string, label: string) => void;
  agentId?: string | null;
  onEditAgentConfig?: (agentId: string) => void;
  onOpenAgentsTab?: (agentId: string) => void;
};

const FILTER_CHIPS: Array<{ id: Exclude<ChatActivityTab, "explore">; label: string }> = [
  { id: "all", label: "All" },
  { id: "thinking", label: "Thinking" },
  { id: "exec", label: "Exec" },
  { id: "todo", label: "Todo" },
  { id: "health", label: "Health" },
];

const KIND_META: Record<ActivityStreamKind, { label: string; icon: string; css: string }> = {
  thinking: { label: "Thinking", icon: "🧠", css: "thinking" },
  exec: { label: "Exec", icon: "⚡", css: "exec" },
  todo: { label: "Todo", icon: "✅", css: "todo" },
  health: { label: "Health", icon: "🩺", css: "health" },
};

/** Maximum number of stream cards rendered at once. Older entries are hidden
 *  behind a "show all" toggle to keep the panel light in long sessions. */
export const VISIBLE_ACTIVITY_CAP = 200;

/** Past this many entries in a single chip, the chip stops printing the raw
 *  number and shows a compact "999+" instead. Raw counts in the thousands
 *  blow up the chip's CSS box and force a layout reflow on every poll —
 *  this is the operator-visible symptom of the unbounded-stream bug. The
 *  `title` attribute still carries the precise count for hover inspection. */
export const CHIP_COUNT_COMPACT_THRESHOLD = 99;

export function formatChipCount(count: number): string {
  // Non-finite values (NaN, -Infinity) can't be sensibly rendered; treat
  // anything beyond the threshold (positive infinity included) as "99+"
  // so a runaway counter never paints a 5-digit number into the chip.
  if (!Number.isFinite(count) || count > CHIP_COUNT_COMPACT_THRESHOLD) {
    return `${CHIP_COUNT_COMPACT_THRESHOLD}+`;
  }
  return String(count);
}

/** Normalize legacy Explore tab → Exec filter. */
export function normalizeActivityFilter(tab: ChatActivityTab): ActivityStreamKind | "all" {
  if (tab === "explore") {
    return "exec";
  }
  return tab;
}

export function formatActivityAge(at: number, nowMs = Date.now()): string {
  if (!Number.isFinite(at) || at <= 0) {
    return "";
  }
  // Message-index timestamps are small ordinals, not wall-clock ms.
  if (at < 1_000_000_000_000) {
    return "";
  }
  const diff = Math.max(0, nowMs - at);
  if (diff < 45_000) {
    return "just now";
  }
  if (diff < 3_600_000) {
    const mins = Math.max(1, Math.floor(diff / 60_000));
    return `${mins} min ago`;
  }
  if (diff < 86_400_000) {
    const hours = Math.max(1, Math.floor(diff / 3_600_000));
    return `${hours}h ago`;
  }
  return new Date(at).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function absoluteTimeTitle(at: number): string {
  if (!Number.isFinite(at) || at < 1_000_000_000_000) {
    return "";
  }
  return new Date(at).toLocaleString();
}

function renderAgentActions(props: ChatActivityPanelProps) {
  const agentId = props.agentId?.trim();
  if (!agentId || (!props.onEditAgentConfig && !props.onOpenAgentsTab)) {
    return nothing;
  }
  return html`
    <div class="chat-activity-panel__agent-actions">
      ${
        props.onEditAgentConfig
          ? html`
              <button
                type="button"
                class="btn subtle chat-activity-panel__agent-btn"
                @click=${() => props.onEditAgentConfig?.(agentId)}
              >
                Edit agent
              </button>
            `
          : nothing
      }
      ${
        props.onOpenAgentsTab
          ? html`
              <button
                type="button"
                class="btn subtle chat-activity-panel__agent-btn"
                @click=${() => props.onOpenAgentsTab?.(agentId)}
              >
                Agents tab
              </button>
            `
          : nothing
      }
    </div>
  `;
}

function renderCardHeader(
  entry: ActivityStreamEntry,
  meta: (typeof KIND_META)[ActivityStreamKind],
  expandable: boolean,
) {
  const age = formatActivityAge(entry.at);
  const abs = absoluteTimeTitle(entry.at);
  return html`
    <span class="chat-activity-panel__badge" data-kind=${entry.kind}>
      <span class="chat-activity-panel__badge-icon" aria-hidden="true">${meta.icon}</span>
      <span class="chat-activity-panel__badge-label">${meta.label}</span>
    </span>
    ${
      age
        ? html`<time
            class="chat-activity-panel__age"
            datetime=${new Date(entry.at).toISOString()}
            title=${abs || nothing}
            >${age}</time
          >`
        : html`<span
            class="chat-activity-panel__age chat-activity-panel__age--source"
            title=${entry.source}
            >${entry.source}</span
          >`
    }
    <span class="chat-activity-panel__summary-text">${entry.summary}</span>
    ${entry.active ? html` <span class="chat-activity-panel__live">live</span> ` : nothing}
    ${
      expandable
        ? html` <span class="chat-activity-panel__expand-hint" aria-hidden="true"></span> `
        : nothing
    }
  `;
}

function renderStreamCard(
  entry: ActivityStreamEntry,
  onOpen?: (key: string, label: string) => void,
  subagents: ChatSubagentCard[] = [],
) {
  const meta = KIND_META[entry.kind];
  const hasBody = Boolean(entry.body?.trim() && entry.body.trim() !== entry.summary.trim());
  const card = subagents.find((c) => c.label === entry.source || c.agentId === entry.source);
  const childKey = entry.kind === "exec" ? card?.childSessionKey : undefined;
  const openBtn =
    childKey && onOpen
      ? html`
          <button
            type="button"
            class="btn subtle chat-activity-panel__open"
            @click=${() => onOpen(childKey, entry.source)}
          >
            Open session tab
          </button>
        `
      : nothing;
  const expandable = hasBody || Boolean(childKey && onOpen);
  const cardClass = [
    "chat-activity-panel__card",
    `chat-activity-panel__card--${meta.css}`,
    entry.active ? "chat-activity-panel__card--active" : "",
    entry.done ? "chat-activity-panel__card--done" : "",
  ]
    .filter(Boolean)
    .join(" ");

  if (!expandable) {
    return html`
      <li class=${cardClass}>
        <div class="chat-activity-panel__summary">${renderCardHeader(entry, meta, false)}</div>
      </li>
    `;
  }

  return html`
    <li class=${cardClass}>
      <details
        class="chat-activity-panel__details"
        ?open=${entry.active && entry.kind === "thinking"}
      >
        <summary class="chat-activity-panel__summary">
          ${renderCardHeader(entry, meta, true)}
        </summary>
        <div class="chat-activity-panel__card-body">
          ${hasBody ? html`<pre class="chat-activity-panel__pre">${entry.body}</pre>` : nothing}
          ${openBtn}
        </div>
      </details>
    </li>
  `;
}

function renderEmpty(filter: ActivityStreamKind | "all", hasAny: boolean) {
  if (!hasAny) {
    return html`
      <p class="muted chat-activity-panel__empty">
        Agent activity will appear here as a single timeline — thinking, exec, todos, and health.
      </p>
    `;
  }
  const label = filter === "all" ? "activity" : KIND_META[filter].label.toLowerCase();
  return html` <p class="muted chat-activity-panel__empty">No ${label} matches this filter.</p> `;
}

export function renderChatActivityPanel(props: ChatActivityPanelProps) {
  const data = collectActivityState({
    messages: props.messages,
    chatToolMessages: props.chatToolMessages,
    stream: props.stream,
    reasoningStream: props.reasoningStream,
    subagents: props.subagents,
    // Dedicated Activity surface — always surface reasoning when present.
    showThinking: true,
    runActive: props.runActive,
    liveRunHint: props.liveRunHint,
    activeFileToolPath: props.activeFileToolPath,
    fleetTodos: props.fleetTodos,
  });

  const filter = normalizeActivityFilter(props.tab);
  const stream = buildActivityStream(data, {
    connected: props.connected,
    subagents: props.subagents,
    runTimeline: props.runTimeline,
    sessionHealth: props.sessionHealth,
    sessionHealthReason: props.sessionHealthReason,
    turnPhase: props.turnPhase,
  });
  const visible = filterActivityStream(stream, filter, props.searchQuery ?? "");

  // Single-pass reduce: 4× `.filter()` walks over the full stream
  // multiplied by the activity-panel re-render frequency added up to
  // 100k+ iterations per poll on long-running orchestrations. One pass
  // gets all 5 chip counts in O(n) instead of O(5n).
  const counts: Record<Exclude<ChatActivityTab, "explore">, number> = {
    all: stream.length,
    thinking: 0,
    exec: 0,
    todo: 0,
    health: 0,
  };
  for (const entry of stream) {
    if (entry.kind === "thinking") counts.thinking += 1;
    else if (entry.kind === "exec") counts.exec += 1;
    else if (entry.kind === "todo") counts.todo += 1;
    else if (entry.kind === "health") counts.health += 1;
  }

  // Cap rendered cards. The stream is already newest-first; slice keeps recency.
  const cappedVisible = visible.slice(0, VISIBLE_ACTIVITY_CAP);
  const hiddenCount = Math.max(0, visible.length - cappedVisible.length);

  return html`
    <div class="chat-activity-panel">
      <div class="chat-activity-panel__toolbar">
        ${renderAgentActions(props)}
        <div class="chat-activity-panel__filters" role="toolbar" aria-label="Activity filters">
          ${FILTER_CHIPS.map((chip) => {
            const active = filter === chip.id;
            const count = counts[chip.id];
            return html`
              <button
                type="button"
                class="chat-activity-panel__chip ${active ? "chat-activity-panel__chip--active" : ""}"
                aria-pressed=${active}
                @click=${() => props.onTabChange(chip.id)}
              >
                ${chip.label}${
                  count > 0
                    ? html` <span
                        class="chat-activity-panel__count"
                        title=${count > CHIP_COUNT_COMPACT_THRESHOLD ? `${count} entries` : nothing}
                        >${formatChipCount(count)}</span
                      >`
                    : nothing
                }
              </button>
            `;
          })}
          ${
            props.onSearchChange
              ? html`
                  <label class="chat-activity-panel__search">
                    <span class="sr-only">Search activity</span>
                    <input
                      type="search"
                      class="chat-activity-panel__search-input"
                      placeholder="Search…"
                      .value=${props.searchQuery ?? ""}
                      @input=${(event: Event) => {
                        const target = event.target as HTMLInputElement;
                        props.onSearchChange?.(target.value);
                      }}
                    />
                  </label>
                `
              : nothing
          }
        </div>
      </div>
      <div
        class="chat-activity-panel__body"
        role="feed"
        aria-label="Activity stream"
        aria-busy=${Boolean(props.runActive)}
      >
        ${
          cappedVisible.length === 0
            ? renderEmpty(filter, stream.length > 0)
            : html`
                <ol class="chat-activity-panel__stream">
                  ${cappedVisible.map((entry) =>
                    renderStreamCard(entry, props.onOpenSubagentSession, props.subagents),
                  )}
                </ol>
                ${
                  hiddenCount > 0
                    ? html`
                        <p
                          class="chat-activity-panel__overflow muted"
                          data-testid="chat-activity-overflow"
                        >
                          ${hiddenCount} older ${hiddenCount === 1 ? "entry" : "entries"} hidden.
                          Use search or filter to narrow.
                        </p>
                      `
                    : nothing
                }
              `
        }
      </div>
    </div>
  `;
}
